import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from './Drawer/SidebarRefactored';
import Header from './Header';
import { openDrawer } from '../../store/reducers/menu';
import dashboardService from '../../services/dashboardService';
import { Danger, InfoCircle } from 'iconsax-react';

const MainLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { drawerOpen } = useSelector((state) => state.menu);
  const [open, setOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    dispatch(openDrawer({ drawerOpen: true }));
  }, [dispatch]);

  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
  }, [drawerOpen, open]);

  // Fetch stats and update plan limit warning state
  const checkSubscriptionLimits = async () => {
    try {
      const result = await dashboardService.getDashboardStats();
      if (result.success && result.data && result.data.subscription) {
        const data = result.data;
        const limits = data.subscription.limits;
        const planName = data.subscription.planName;
        const activeCaregivers = data.caregivers.active;
        const activePatients = data.patients.active;

        const newAlerts = [];

        // Check Caregivers Limits
        if (limits && limits.maxCaregivers !== -1) {
          if (activeCaregivers >= limits.maxCaregivers) {
            newAlerts.push({
              id: 'caregivers-limit',
              type: 'critical',
              message: `You have reached 100% of your Caregiver Seats limit (${activeCaregivers} / ${limits.maxCaregivers} seats) for the ${planName} Plan. Onboarding or activating more caregivers is currently blocked.`
            });
          } else if (activeCaregivers >= Math.ceil(limits.maxCaregivers * 0.8)) {
            newAlerts.push({
              id: 'caregivers-warning',
              type: 'warning',
              message: `You have reached 80% of your Caregiver Seats limit (${activeCaregivers} / ${limits.maxCaregivers} seats) for the ${planName} Plan. Upgrade your plan to prevent onboarding disruptions.`
            });
          }
        }

        // Check Patients Limits
        if (limits && limits.maxPatients !== -1) {
          if (activePatients >= limits.maxPatients) {
            newAlerts.push({
              id: 'patients-limit',
              type: 'critical',
              message: `You have reached 100% of your Patient Profiles limit (${activePatients} / ${limits.maxPatients} profiles) for the ${planName} Plan. Onboarding or activating more patients is currently blocked.`
            });
          } else if (activePatients >= Math.ceil(limits.maxPatients * 0.8)) {
            newAlerts.push({
              id: 'patients-warning',
              type: 'warning',
              message: `You have reached 80% of your Patient Profiles limit (${activePatients} / ${limits.maxPatients} profiles) for the ${planName} Plan. Upgrade your plan to prevent onboarding disruptions.`
            });
          }
        }

        setAlerts(newAlerts);
        if (data.subscription.features) {
          setFeatures(data.subscription.features);
        }
      }
    } catch (error) {
      console.error('Failed to verify subscription limits:', error);
    }
  };

  // Run limits verification on mount, on route changes, and on page visibility focus
  useEffect(() => {
    checkSubscriptionLimits();
  }, [location.pathname]);

  useEffect(() => {
    // Listen to custom subscription update events (triggered when resources are created/activated/deactivated)
    window.addEventListener('subscription-update', checkSubscriptionLimits);

    const handleVisibilityChange = () => {
      if (!document.hidden) checkSubscriptionLimits();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('subscription-update', checkSubscriptionLimits);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const sidebarWidth = collapsed ? 64 : 256;

  return (
    <div className="flex min-h-screen">
      <Sidebar open={open} onToggle={handleDrawerToggle} onCollapsedChange={setCollapsed} features={features} />
      <div className="flex flex-col flex-1">
        <Header open={open} sidebarWidth={sidebarWidth} collapsed={collapsed} />
        <main 
          className="flex-1 p-6 pt-20 transition-all duration-300 bg-gray-50 flex flex-col space-y-4"
          style={{
            marginLeft: open ? `${sidebarWidth}px` : '0'
          }}
        >
          {/* Subscription Alert Banners Stack - Only show on dashboard */}
          {(location.pathname === '/' || location.pathname === '/dashboard') && alerts.length > 0 && (
            <div className="space-y-2 w-full animate-in fade-in slide-in-from-top duration-300">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-start gap-3 p-4 rounded-lg border text-sm shadow-sm transition-all duration-300 ${
                    alert.type === 'critical'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}
                >
                  <div className="mt-0.5 shrink-0">
                    {alert.type === 'critical' ? (
                      <Danger size={18} className="text-red-600" />
                    ) : (
                      <InfoCircle size={18} className="text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-xs md:text-sm">{alert.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex-1">
            <Outlet context={{ features }} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;